// EmailSettings holds one row per sending address. These pick the row a caller
// means, so the ~30 sites that used to read a single relation stay one-liners.

export interface AddressRow {
	isPrimary: boolean
	isEnabled: boolean
	primaryEmail: string
	brandedInboundEmailAddress: string
	domain: string
}

export function primaryAddress<T extends { isPrimary: boolean }>(
	rows: readonly T[] | null | undefined,
): T | undefined {
	return rows?.find((row) => row.isPrimary) ?? rows?.[0]
}

export function enabledAddresses<T extends { isEnabled: boolean }>(
	rows: readonly T[] | null | undefined,
): T[] {
	return (rows ?? []).filter((row) => row.isEnabled)
}

// "Is the shared inbox set up at all", the question the old singular
// `emailSettings.isEnabled` was really asking.
export function hasEnabledEmail<T extends { isEnabled: boolean }>(
	rows: readonly T[] | null | undefined,
): boolean {
	return enabledAddresses(rows).length > 0
}

// An address sender name replaces the teammate's name, so a team can reply as one name.
export function teammateSenderName({
	senderName,
	userName,
	workspaceName,
}: {
	senderName: string | null | undefined
	userName: string | null | undefined
	workspaceName: string
}): string {
	const custom = senderName?.trim()
	if (custom) return custom
	const firstName = userName?.trim().split(/\s+/)[0]
	return firstName ? `${firstName} (${workspaceName})` : workspaceName
}

export function resolveAutoReplySenderName({
	autoReplySenderName,
	senderName,
	workspaceName,
}: {
	autoReplySenderName: string | null | undefined
	senderName: string | null | undefined
	workspaceName: string
}): string {
	return autoReplySenderName?.trim() || senderName?.trim() || workspaceName
}

export function addressById<T extends { id: string }>(
	rows: readonly T[] | null | undefined,
	id: string | null | undefined,
): T | undefined {
	return id ? rows?.find((row) => row.id === id) : undefined
}

// The address a reply on a thread goes out from. Disabled rows are skipped
// because a disabled inbox would strand the customer's answer.
export function threadSenderAddress<
	T extends { id: string; isPrimary: boolean; isEnabled: boolean },
>(
	rows: readonly T[] | null | undefined,
	{
		chosenId,
		threadAddressId,
		portalAddressId,
	}: {
		chosenId?: string | null
		threadAddressId?: string | null
		portalAddressId?: string | null
	},
): T | undefined {
	const enabled = enabledAddresses(rows)
	return (
		addressById(enabled, chosenId) ??
		addressById(enabled, threadAddressId) ??
		addressById(enabled, portalAddressId) ??
		enabled.find((row) => row.isPrimary) ??
		primaryAddress(rows)
	)
}

// Rows on one domain share a Postmark domain and its DNS, so adding an address
// to a domain that is already verified needs no new records.
export function verifiedDomains<
	T extends {
		domain: string
		isTxtDnsActive: boolean
		isCnameDnsActive: boolean
	},
>(rows: readonly T[] | null | undefined): string[] {
	return [
		...new Set(
			(rows ?? [])
				.filter((row) => row.isTxtDnsActive && row.isCnameDnsActive)
				.map((row) => row.domain)
				.filter(Boolean),
		),
	]
}

export function rowForDomain<
	T extends { domain: string; postmarkDomainId: string },
>(rows: readonly T[] | null | undefined, domain: string): T | undefined {
	return (rows ?? []).find((row) => row.domain === domain)
}
